import {SelectWithCheckboxes} from '@rdcomponents/select-with-checkboxes/select-with-checkboxes';
import {Text} from '@rdcomponents/text/text';
import {Button, Space} from 'antd';
import {useEffect, useState} from 'react';

import {Locale} from '../../../../../../../provider/locale/locale';
import {WeekDaysEnum} from '../../../../../../../provider/locale/locale-context-const';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {CompanyKeyEnum} from '../../../../../../../service/company-v2/company-const';
import {CompanyErrorType, CompanyFormType} from '../../../../../../../service/company-v2/company-type';
import {WorkingDaysType} from '../../../../../../../service/company-v2/types/company-working-time-type';
import {Form, FormInstance} from '../../../../../../../typings/antd';
import {useRefreshId} from '../../../../../../../util/hook';
import {getWorkingDaysObject} from '../../../company-form-helper';
import {OpeningSoonControl} from '../opening-soon-form-item/opening-soon-control/opening-soon-control';
import {weekDayAllNameValuesMap} from '../working-time-const';

import {DaysGroup} from './days-group/days-group';
import {useOpeningSoonFormItem} from './use-opening-soon-form-item';
import {
    emptyWeekday,
    getAvailableDays,
    getEmptyWeekdays,
    getErrorForGroupFirstDayWithServerError,
    getSortedWeekdays,
    getSortedWorkingDays,
    getUpdatedWeekdays,
} from './workdays-form-item-helper';
import * as daysGroupStyles from './days-group/days-group.scss';
import * as styles from './workdays-form-item.scss';

const DEFAULT_VALUE = getWorkingDaysObject(Object.values(WeekDaysEnum).map((day) => [`day${day}`, null]));

type PropsType = {
    value?: WorkingDaysType;
    onChange?: (value: WorkingDaysType) => void;
    errorMessage?: CompanyErrorType['workingHours'] | CompanyErrorType['extraWorkingHours'] | null;
    openingDateErrorMessage?: CompanyErrorType['openingDate'];
    catalogId?: number;
};

export function WorkdaysFormItem(props: PropsType): JSX.Element {
    const {value: workingDays = DEFAULT_VALUE, onChange, errorMessage, openingDateErrorMessage, catalogId} = props;

    const [isAddButtonShown, setIsAddButtonShown] = useState<boolean>(false);

    const {handleOpeningSoonSwitchChange, isOpeningSoonSwitchOn, openingDateRules} = useOpeningSoonFormItem();

    const temporaryClosed = Form.useWatch<CompanyKeyEnum.TemporaryClosed, FormInstance<CompanyFormType>>(
        CompanyKeyEnum.TemporaryClosed
    );

    const {refresh} = useRefreshId();
    const {getLocalizedString} = useLocale();

    function onNewDayAdd(weekdays: Array<WeekDaysEnum>) {
        setIsAddButtonShown(false);
        onChange?.({...workingDays, ...getEmptyWeekdays(weekdays)});
    }

    function handleOnChange(newValue: WorkingDaysType) {
        onChange?.(newValue);
        refresh();
    }

    useEffect(() => {
        if (getSortedWorkingDays(workingDays).length === 0) {
            onChange?.(getWorkingDaysObject(Object.values(WeekDaysEnum).map((day) => [`day${day}`, emptyWeekday])));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortedWorkingDays = getSortedWorkingDays(workingDays);
    const showOpeningSoon = temporaryClosed && !catalogId;
    const openingSoonControl = showOpeningSoon ? (
        <OpeningSoonControl
            errorMessage={openingDateErrorMessage}
            isSwitchOn={isOpeningSoonSwitchOn}
            onSwitchChange={handleOpeningSoonSwitchChange}
            openingDateRules={openingDateRules}
            showCatalogHint
        />
    ) : null;

    return (
        <>
            {openingSoonControl && sortedWorkingDays.length === 0 ? (
                <div className={daysGroupStyles.DaysGroup_timeBlock}>{openingSoonControl}</div>
            ) : null}
            {sortedWorkingDays.map((days, index) => (
                <DaysGroup
                    availableDays={getAvailableDays(workingDays)}
                    days={days}
                    errorMessage={getErrorForGroupFirstDayWithServerError(errorMessage, days.daysOfWeek)}
                    isWorkingHoursRequired={!temporaryClosed}
                    key={days.daysOfWeek[0]}
                    onChange={(breaks) => {
                        handleOnChange(getUpdatedWeekdays(workingDays, days.daysOfWeek, breaks));
                    }}
                    openingSoonSlot={index === 0 ? openingSoonControl : null}
                    updateDays={(weekdays) => {
                        handleOnChange(getSortedWeekdays(workingDays, days, weekdays));
                    }}
                />
            ))}
            {isAddButtonShown && (
                <div className={daysGroupStyles.DaysGroup_timeBlock}>
                    <div className={daysGroupStyles.DaysGroup_weekDayBlock}>
                        <Text
                            className={daysGroupStyles.DaysGroup_text}
                            stringKey="COMPANY_FORM__WORKING_TIME__DAYS_OF_THE_WEEK"
                        />
                        <SelectWithCheckboxes<WeekDaysEnum>
                            hideFooter
                            initialValue={[]}
                            onConfirm={onNewDayAdd}
                            options={Object.entries(weekDayAllNameValuesMap)
                                .filter(([weekday]) => getAvailableDays(workingDays).includes(weekday as WeekDaysEnum))
                                .map(([weekday, stringKey]) => ({
                                    value: weekday as WeekDaysEnum,
                                    label: getLocalizedString(stringKey),
                                }))}
                            withoutIndeterminate
                            wrapperClassName={daysGroupStyles.DaysGroup_customSelect}
                        />
                    </div>
                </div>
            )}
            {getAvailableDays(workingDays).length > 0 && (
                <Space align="baseline" className={styles.WorkdaysFormItem_add} size="middle">
                    <Button onClick={() => setIsAddButtonShown(true)}>
                        <Locale stringKey="COMPANY_FORM__WORKING_TIME__ADD_DAYS" />
                    </Button>
                    <Text secondary stringKey="COMPANY_FORM__WORKING_TIME__DAYS_WHICH_NO_TIME" />
                </Space>
            )}
        </>
    );
}
